import {
  ActionIcon,
  Button,
  Card,
  Center,
  Group,
  Modal,
  MultiSelect,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { Calendar, DateTimePicker, getTimeRange } from "@mantine/dates";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useForm } from "@tanstack/react-form-start";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import "dayjs/locale/de";
import type { EventInput } from "@lib/db/schemas";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNotification } from "@webapp/hooks/useNotification";
import {
  createEventFn,
  deleteEventFn,
  listAllEventsFn,
  updateEventFn,
} from "@webapp/server/functions/events";
import { listTeamsFn } from "@webapp/server/functions/teams";
import { Plus, SquarePen, Trash2 } from "lucide-react";

dayjs.locale("de");

const defaultFormValues = {
  id: undefined as string | undefined,
  title: "",
  description: "",
  startDate: null as Date | null,
  endDate: undefined as Date | undefined,
  location: "",
  variant: "",
  teamIds: [] as string[] | undefined,
};

function EventsPage() {
  const [opened, { open, close }] = useDisclosure(false);

  const notification = useNotification();
  const {
    data: eventsData,
    isLoading,
    refetch,
  } = useQuery({ queryKey: ["events", "list"], queryFn: () => listAllEventsFn() });
  const { data: teams } = useQuery({ queryKey: ["teams", "list"], queryFn: () => listTeamsFn() });
  const form = useForm({
    defaultValues: defaultFormValues,
    onSubmit: async ({ value }) => {
      const currentEditingId = value.id;
      if (!value.startDate) {
        return;
      }

      if (currentEditingId) {
        updateMutation.mutate({
          id: currentEditingId,
          data: {
            type: "event" as const,
            title: value.title,
            description: value.description || null,
            startDate: value.startDate.toISOString(),
            endDate: value.endDate ? value.endDate.toISOString() : undefined,
            location: value.location || null,
            variant: value.variant || null,
            teamIds: value.teamIds || undefined,
          },
        });
      } else {
        createMutation.mutate({
          type: "event" as const,
          title: value.title,
          description: value.description || undefined,
          startDate: value.startDate.toISOString(),
          endDate: value.endDate ? value.endDate.toISOString() : undefined,
          location: value.location || undefined,
          variant: value.variant || undefined,
          teamIds: value.teamIds || undefined,
        });
      }
    },
  });
  const editingId = form.getFieldValue("id");

  const events = eventsData?.items || [];
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Create a set of all dates with events (excluding the one being edited)
  const eventDates = new Set<string>();
  for (const event of events) {
    // Skip the event being edited
    if (editingId && event.id === editingId) continue;

    const start = dayjs(event.startDate);
    const end = event.endDate ? dayjs(event.endDate) : start;

    // Add all dates in the range
    let current = start;
    while (current.isBefore(end, "day") || current.isSame(end, "day")) {
      eventDates.add(current.format("YYYY-MM-DD"));
      current = current.add(1, "day");
    }
  }
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createEventFn>[0]["data"]) => createEventFn({ data }),
    onSuccess: () => {
      refetch();
      close();
      resetForm();
      notification.success("Termin wurde erfolgreich erstellt");
    },
    onError: () => {
      notification.error({ message: "Termin konnte nicht erstellt werden" });
    },
  });
  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateEventFn>[0]["data"]) => updateEventFn({ data }),
    onSuccess: () => {
      refetch();
      close();
      resetForm();
      notification.success("Termin wurde erfolgreich aktualisiert");
    },
    onError: () => {
      notification.error({ message: "Termin konnte nicht aktualisiert werden" });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (data: Parameters<typeof deleteEventFn>[0]["data"]) => deleteEventFn({ data }),
    onSuccess: () => {
      refetch();
      close();
      resetForm();
      notification.success("Termin wurde erfolgreich gelöscht");
    },
    onError: () => {
      notification.error({ message: "Termin konnte nicht gelöscht werden" });
    },
  });
  const resetForm = () => {
    form.reset();
  };

  const handleEdit = (event: EventInput) => {
    form.setFieldValue("id", event.id);
    form.setFieldValue("title", event.title);
    form.setFieldValue("description", event.description || "");
    form.setFieldValue("startDate", new Date(event.startDate));
    form.setFieldValue("endDate", event.endDate ? new Date(event.endDate) : undefined);
    form.setFieldValue("location", event.location || "");
    form.setFieldValue("variant", event.variant || "");
    form.setFieldValue("teamIds", event.teamIds || []);
    open();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Möchten Sie diesen Termin wirklich löschen?")) {
      deleteMutation.mutate({ id });
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Termine</Title>
        <Button
          onClick={() => {
            resetForm();
            open();
          }}
          leftSection={<Plus />}
          visibleFrom="sm"
        >
          Neuer Termin
        </Button>
        <ActionIcon
          onClick={() => {
            resetForm();
            open();
          }}
          hiddenFrom="sm"
          variant="filled"
          radius="xl"
        >
          <Plus size={20} />
        </ActionIcon>
      </Group>

      <Center p="md">
        <Calendar
          numberOfColumns={isMobile ? 1 : 2}
          getDayProps={(date) => {
            const dateStr = dayjs(date).format("YYYY-MM-DD");
            if (eventDates.has(dateStr)) {
              return {
                style: {
                  backgroundColor: "var(--mantine-color-turquoise-6)",
                  border: "1px solid var(--mantine-color-turquoise-8)",
                  color: "var(--mantine-color-white)",
                },
              };
            }
            return {};
          }}
        />
      </Center>

      {isLoading ? (
        <Text>Laden...</Text>
      ) : events.length > 0 ? (
        <>
          <Card withBorder bg="white" p={0} radius="md" visibleFrom="sm">
            <Table striped highlightOnHover horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Titel</Table.Th>
                  <Table.Th>Terminart</Table.Th>
                  <Table.Th>Start</Table.Th>
                  <Table.Th>Ende</Table.Th>
                  <Table.Th>Ort</Table.Th>
                  <Table.Th>Aktionen</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {events.map((event) => (
                  <Table.Tr key={event.id}>
                    <Table.Td>{event.title}</Table.Td>
                    <Table.Td>{event.variant || "-"}</Table.Td>
                    <Table.Td>{dayjs(event.startDate).format("DD.MM.YYYY HH:mm")}</Table.Td>
                    <Table.Td>
                      {event.endDate ? dayjs(event.endDate).format("DD.MM.YYYY HH:mm") : "-"}
                    </Table.Td>
                    <Table.Td>{event.location || "-"}</Table.Td>
                    <Table.Td>
                      <Button visibleFrom="sm" size="xs" onClick={() => handleEdit(event)}>
                        Bearbeiten
                      </Button>
                      <ActionIcon
                        hiddenFrom="sm"
                        variant="filled"
                        radius="xl"
                        onClick={() => handleEdit(event)}
                      >
                        <SquarePen size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>

          <SimpleGrid cols={{ base: 1, sm: 1 }} spacing="md" hiddenFrom="sm">
            {events.map((event) => (
              <Card key={event.id} shadow="sm" p="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group justify="space-between" align="flex-start">
                    <Title order={4}>{event.title}</Title>
                    <ActionIcon
                      color="blumine"
                      variant="filled"
                      onClick={() => handleEdit(event)}
                      radius="xl"
                    >
                      <SquarePen size={16} />
                    </ActionIcon>
                  </Group>
                  <Stack gap="xs">
                    {event.variant && (
                      <div>
                        <Text size="xs" fw={500} c="dimmed">
                          Terminart
                        </Text>
                        <Text size="sm">{event.variant}</Text>
                      </div>
                    )}
                    <div>
                      <Text size="xs" fw={500} c="dimmed">
                        Zeitraum
                      </Text>
                      <Text size="sm">
                        {event.endDate && dayjs(event.startDate).isSame(dayjs(event.endDate), "day")
                          ? `${dayjs(event.startDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(event.endDate).format("HH:mm")}`
                          : event.endDate
                            ? `${dayjs(event.startDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(event.endDate).format("DD.MM.YYYY HH:mm")}`
                            : dayjs(event.startDate).format("DD.MM.YYYY HH:mm")}
                      </Text>
                    </div>
                    {event.location && (
                      <div>
                        <Text size="xs" fw={500} c="dimmed">
                          Ort
                        </Text>
                        <Text size="sm">{event.location}</Text>
                      </div>
                    )}
                  </Stack>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        </>
      ) : (
        <Text c="dimmed">Noch keine Termine vorhanden. Erstellen Sie einen neuen Eintrag.</Text>
      )}

      <Modal
        opened={opened}
        onClose={() => {
          close();
          resetForm();
        }}
        title={editingId ? "Termin bearbeiten" : "Termin hinzufügen"}
        size={isMobile ? "100%" : "lg"}
        fullScreen={isMobile}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.Subscribe selector={(state) => state.values}>
            {(formData) => (
              <Stack>
                <form.Field name="title">
                  {(field) => (
                    <TextInput
                      label="Titel"
                      placeholder="z.B. Heimspiel gegen Team X"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      required
                    />
                  )}
                </form.Field>

                <form.Field name="variant">
                  {(field) => (
                    <TextInput
                      label="Terminart"
                      placeholder="z.B. Spiel, Training, Versammlung"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>

                <form.Field name="startDate">
                  {(field) => (
                    <DateTimePicker
                      label="Startdatum & Uhrzeit"
                      placeholder="Beginn wählen"
                      locale="de"
                      value={field.state.value}
                      onChange={(date) => field.handleChange(date ? new Date(date) : null)}
                      valueFormat="D MMMM YYYY - HH:mm [Uhr]"
                      getDayProps={(date) => {
                        const dateStr = dayjs(date).format("YYYY-MM-DD");
                        if (eventDates.has(dateStr)) {
                          return {
                            style: {
                              backgroundColor: "var(--mantine-color-turquoise-4)",
                              border: "1px solid var(--mantine-color-turquoise-6)",
                              color: "var(--mantine-color-white)",
                            },
                          };
                        }
                        return {};
                      }}
                      required
                      highlightToday
                      timePickerProps={{
                        withDropdown: true,
                        format: "24h",
                        presets: getTimeRange({
                          startTime: "09:00:00",
                          endTime: "14:00:00",
                          interval: "00:30:00",
                        }),
                      }}
                      presets={
                        isMobile
                          ? undefined
                          : [
                              {
                                value: dayjs().add(1, "day").format("YYYY-MM-DD HH:mm:ss"),
                                label: "Morgen",
                              },
                              {
                                value: dayjs().add(1, "week").format("YYYY-MM-DD HH:mm:ss"),
                                label: "Nächste Woche",
                              },
                              {
                                value: dayjs().add(1, "month").format("YYYY-MM-DD HH:mm:ss"),
                                label: "Nächster Monat",
                              },
                            ]
                      }
                    />
                  )}
                </form.Field>

                <form.Field name="endDate">
                  {(field) => (
                    <DateTimePicker
                      label="Enddatum & Uhrzeit"
                      placeholder="Ende wählen"
                      locale="de"
                      value={field.state.value}
                      onChange={(date) => field.handleChange(date ? new Date(date) : undefined)}
                      valueFormat="D MMMM YYYY - HH:mm [Uhr]"
                      getDayProps={(date) => {
                        const dateStr = dayjs(date).format("YYYY-MM-DD");
                        if (eventDates.has(dateStr)) {
                          return {
                            style: {
                              backgroundColor: "var(--mantine-color-turquoise-4)",
                              border: "1px solid var(--mantine-color-turquoise-6)",
                              color: "var(--mantine-color-white)",
                            },
                          };
                        }
                        return {};
                      }}
                      clearable
                      highlightToday
                      timePickerProps={{
                        withDropdown: true,
                        format: "24h",
                        presets: getTimeRange({
                          startTime: "16:00:00",
                          endTime: "22:00:00",
                          interval: "00:30:00",
                        }),
                      }}
                      presets={
                        isMobile
                          ? undefined
                          : [
                              {
                                value: dayjs().add(1, "day").format("YYYY-MM-DD HH:mm:ss"),
                                label: "Morgen",
                              },
                              {
                                value: dayjs().add(1, "week").format("YYYY-MM-DD HH:mm:ss"),
                                label: "Nächste Woche",
                              },
                              {
                                value: dayjs().add(1, "month").format("YYYY-MM-DD HH:mm:ss"),
                                label: "Nächster Monat",
                              },
                            ]
                      }
                    />
                  )}
                </form.Field>

                <form.Field name="location">
                  {(field) => (
                    <TextInput
                      label="Ort"
                      placeholder="z.B. Sporthalle Müllheim"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>

                <form.Field name="description">
                  {(field) => (
                    <Textarea
                      label="Beschreibung"
                      placeholder="Zusätzliche Informationen..."
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      minRows={3}
                    />
                  )}
                </form.Field>

                <form.Field name="teamIds">
                  {(field) => (
                    <MultiSelect
                      label="Team (optional)"
                      placeholder="Dazugehörige Teams auswählen"
                      data={
                        teams
                          ? teams.items.map((team) => ({ value: team.id, label: team.name }))
                          : []
                      }
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value || [])}
                      clearable
                      hidePickedOptions
                    />
                  )}
                </form.Field>

                <Group justify="space-between" mt="md">
                  {editingId && (
                    <>
                      <ActionIcon
                        hiddenFrom="sm"
                        color="red"
                        variant="light"
                        onClick={() => handleDelete(editingId)}
                        loading={deleteMutation.isPending}
                        size="lg"
                      >
                        <Trash2 />
                      </ActionIcon>
                      <Button
                        visibleFrom="sm"
                        color="red"
                        variant="light"
                        onClick={() => handleDelete(editingId)}
                        loading={deleteMutation.isPending}
                      >
                        Löschen
                      </Button>
                    </>
                  )}
                  <Group gap="xs" ms="auto">
                    <Button variant="light" type="button" onClick={close}>
                      Abbrechen
                    </Button>
                    <Button
                      variant="filled"
                      type="submit"
                      loading={createMutation.isPending || updateMutation.isPending}
                      disabled={!formData.title || !formData.startDate}
                    >
                      {editingId ? "Aktualisieren" : "Erstellen"}
                    </Button>
                  </Group>
                </Group>
              </Stack>
            )}
          </form.Subscribe>
        </form>
      </Modal>
    </Stack>
  );
}

export const Route = createFileRoute("/admin/_layout/events")({
  component: EventsPage,
});
